import { AnimatePresence, motion } from 'framer-motion';
import { type PropsWithChildren, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ArrowLeftIcon, Avatar, ChevronRightIcon, GridIcon, tokens } from '@treams/treams-ui';

import TalentInsightsCard from './TalentInsightsCard';

const HEADER_TRANSITION = {
	duration: 0.15,
	ease: 'easeInOut',
} as const;

const BackLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}

	svg {
		display: block;
		width: 18px;
		height: 18px;
		color: currentColor;
	}
`;

const Eyebrow = styled(Flex)`
	align-items: center;
	gap: 4px;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.h3}px;
	font-weight: ${tokens.typography.weights.medium};

	> svg {
		display: block;
		width: 20px;
		height: 20px;
		color: currentColor;
	}
`;

const TitleRow = styled(Flex)`
	align-items: center;
	min-width: 0;
	gap: 4px;
`;

const PageTitle = styled.h1`
	margin: 0;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.title}px;
	font-weight: ${tokens.typography.weights.semibold};
	line-height: ${tokens.typography.lineHeights.heading};
`;

const Divider = styled(Box)`
	height: 1px;
	background: ${tokens.border.subtle};
`;

const MotionBlock = styled(motion.div)`
	overflow: hidden;
`;

const PlacementHelp = styled.p`
	margin: 0;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	font-weight: ${tokens.typography.weights.regular};
`;

const PlacementHelpAction = styled.button`
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	color: ${tokens.text.primary};
	font-weight: ${tokens.typography.weights.medium};
	cursor: pointer;
	text-decoration: underline;
`;

const GroupNameButton = styled.button`
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	color: inherit;
	cursor: pointer;
	text-decoration: underline;
`;

const EyebrowCopy = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	min-width: 0;
`;

const GroupChevron = styled.span`
	flex-shrink: 0;
	display: flex;
	color: ${tokens.icon.secondary};

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
`;

const AnimateHeight = (props: { children: ReactNode }) => {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		const updateHeight = () => setHeight(el.getBoundingClientRect().height);
		updateHeight();

		const observer = new ResizeObserver(updateHeight);
		observer.observe(el);
		return () => observer.disconnect();
	}, [props.children]);

	return (
		<motion.div
			initial={{ height: 0, opacity: 0 }}
			animate={{ height, opacity: 1 }}
			exit={{ height: 0, opacity: 0 }}
			transition={HEADER_TRANSITION}
			style={{ overflow: 'hidden' }}
		>
			<div ref={ref}>{props.children}</div>
		</motion.div>
	);
};

export default function TalentReviewPageHeader(
	props: PropsWithChildren<{
		backLink: string;
		title: string;
		pageTitle?: string;
		user?: Pick<graphql.User, 'id' | 'firstName' | 'lastName'> & {
			avatar?: graphql.User['avatar'];
		};
		groupName?: string;
		templateID?: string;
		groupID?: string;
		localDate?: string;
		placement?: { row: number; column: number };
		/** Hide AI insights: they describe the person now, not a historical day. */
		past?: boolean;
		onClearFocus?: () => void;
		onStartPlacementHelp?: () => void;
	}>,
) {
	const { t } = useTranslation();
	const groupName = props.groupName ?? props.title;

	return (
		<Box>
			<Helmet>
				<title>{props.pageTitle ?? props.title}</title>
			</Helmet>
			<MotionBlock layout transition={HEADER_TRANSITION}>
				<Box mt={4}>
					<BackLink to={props.backLink}>
						<ArrowLeftIcon />
						{t('talentReviews.v2.header.back')}
					</BackLink>
				</Box>
			</MotionBlock>
			<MotionBlock layout transition={HEADER_TRANSITION}>
				<Eyebrow mt={4}>
					<GridIcon />
					{props.user ? (
						<EyebrowCopy>
							<span>
								<Trans
									i18nKey="talentReviews.v2.header.eyebrowGroup"
									values={{ group: groupName }}
									components={{
										group: <GroupNameButton type="button" onClick={props.onClearFocus} />,
									}}
								/>
							</span>
							<GroupChevron aria-hidden>
								<ChevronRightIcon size={16} />
							</GroupChevron>
						</EyebrowCopy>
					) : (
						t('talentReviews.v2.header.eyebrow')
					)}
				</Eyebrow>
			</MotionBlock>
			<MotionBlock layout transition={HEADER_TRANSITION}>
				<TitleRow mt={3}>
					<AnimatePresence initial={false}>
						{props.user && (
							<motion.div
								key="header-avatar"
								initial={{ width: 0, opacity: 0 }}
								animate={{ width: 40, opacity: 1 }}
								exit={{ width: 0, opacity: 0 }}
								transition={HEADER_TRANSITION}
								style={{ overflow: 'hidden', flexShrink: 0, lineHeight: 0 }}
							>
								<Avatar size={36} user={props.user} />
							</motion.div>
						)}
					</AnimatePresence>
					<PageTitle>{props.title}</PageTitle>
				</TitleRow>
			</MotionBlock>

			<AnimatePresence initial={false}>
				{props.user && !props.past && props.templateID && props.groupID && props.localDate && (
					<AnimateHeight key="insights">
						<Box pt={3}>
							<TalentInsightsCard
								user={props.user}
								templateID={props.templateID}
								groupID={props.groupID}
								localDate={props.localDate}
								row={props.placement?.row}
								column={props.placement?.column}
							/>
						</Box>
					</AnimateHeight>
				)}
				{props.user && props.onStartPlacementHelp && (
					<AnimateHeight key="placement-help">
						<Box pt={3}>
							<PlacementHelp>
								{t('talentReviews.v2.header.placementHelp', {
									name: props.user.firstName,
								})}{' '}
								<PlacementHelpAction type="button" onClick={props.onStartPlacementHelp}>
									{t('talentReviews.v2.header.placementHelpAction')}
								</PlacementHelpAction>
							</PlacementHelp>
						</Box>
					</AnimateHeight>
				)}
			</AnimatePresence>

			<MotionBlock layout transition={HEADER_TRANSITION}>
				<Divider mt={4} />
			</MotionBlock>
			<MotionBlock layout transition={HEADER_TRANSITION}>
				{props.children}
			</MotionBlock>
		</Box>
	);
}
